import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Shield, Thermometer, Target, Feather, Star, Users, Car, Phone, Award, Briefcase } from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  const sixPillars = [
    { icon: Car, title: 'Concierge-Driven Car Service', description: 'Executive black SUVs, sedans, Sprinters, and exotics — driven by professionals who treat every trip as a personal engagement.' },
    { icon: Briefcase, title: 'Auto Lifestyle Consulting', description: 'Vehicle acquisitions, monetization strategies, fleet management, and one-on-one advisory sessions with your Auto Concierge.' },
    { icon: Users, title: 'Membership & Access', description: 'Three-tier membership system (Lobby, Condo, Penthouse) providing escalating access to ACE services, priority, and your concierge\'s time.' },
    { icon: Star, title: 'AuctionWell Intelligence', description: 'Proprietary market intelligence platform giving clients access to the same data used by professional dealers and auction buyers.' },
    { icon: Award, title: 'Driver Training & Standards', description: 'ACE Certified Concierge Driver (ACCD) program establishing the professional standard for luxury ground transportation in Atlanta.' },
    { icon: Shield, title: 'Partner Network', description: 'Curated network of service providers — detailing, maintenance, storage, insurance — vetted to the ACE standard.' },
  ];

  const fourPillars = [
    { icon: Shield, title: 'Preparation', description: 'Ready before the client arrives — every time, without exception. Vehicle inspected, detailed, temperature set, amenities stocked.' },
    { icon: Thermometer, title: 'Environment', description: 'Temperature, scent, sound — the cabin environment is controlled before you enter. The experience begins at the door.' },
    { icon: Target, title: 'Intentional Service', description: 'Nothing left to chance. Every detail considered before the moment arises. Your preferences remembered, your needs anticipated.' },
    { icon: Feather, title: 'Friction Removal', description: 'Anticipate every need. Eliminate every ask. Deliver relief. The highest form of service is the one the client never has to request.' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero */}
      <div className="bg-black text-white py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <p className="text-sm text-red-500 font-semibold tracking-widest mb-4">ABOUT ACE</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="about-hero-title">
              You're <em className="text-red-500 not-italic">Driven.</em> We're <em className="text-red-500 not-italic">Driving.</em>
            </h1>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              Auto Concierge Enterprises isn't a car service. It's a mobility platform and auto lifestyle consultancy — built by JD Lawrence in Sandy Springs, Georgia to serve Metro Atlanta's most intentional clients.
            </p>
          </div>
        </div>
      </div>

      {/* JD Lawrence Bio */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-sm text-red-600 font-semibold tracking-widest mb-3">FOUNDER</p>
            <h2 className="text-4xl font-bold text-black mb-6" data-testid="founder-heading">JD Lawrence</h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                JD Lawrence is the founder of Auto Concierge Enterprises LLC, based in Sandy Springs, Georgia. What started as a personal commitment to excellence in luxury ground transportation has grown into a full-service auto lifestyle platform — combining concierge-driven car service, vehicle acquisition consulting, fleet management, and membership-based access to JD's time, network, and expertise.
              </p>
              <p>
                Born and raised in Atlanta, JD built ACE on a simple belief: <strong>luxury is not defined by the vehicle — it's defined by preparation, intentional service, control of the environment, and the removal of friction for the client.</strong> That philosophy — now codified as the Four Pillars — drives every interaction, every booking, and every consultation.
              </p>
              <p>
                Before founding ACE, JD spent years studying the intersection of Atlanta's unique car culture, the luxury transportation industry, and the needs of high-performing professionals who demand more than what the standard market offers. He recognized that the gap wasn't in the vehicles — it was in the standard of service surrounding them.
              </p>
              <p>
                Today, JD personally oversees every aspect of ACE operations. Penthouse members have direct, unlimited access to his time. Condo members receive monthly advisory sessions. Every client — regardless of tier — interacts with someone who knows their name, their preferences, and their standard before the car door opens.
              </p>
            </div>
          </div>
          <div className="space-y-6">
            <Card className="p-8 bg-black text-white border-0">
              <p className="text-sm text-red-500 font-semibold tracking-wider mb-4">THE ACE PHILOSOPHY</p>
              <h3 className="text-2xl font-bold mb-4">Driven or Driving</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                At ACE, we see our clients through a simple lens: they're either being <strong className="text-white">Driven</strong> — in one of our vehicles, experiencing concierge-level ground transportation — or they're <strong className="text-red-400">Driving</strong> — making moves in their auto lifestyle with ACE as their advisory partner.
              </p>
              <p className="text-gray-400 text-sm">
                Whether you need a Maybach for a corporate arrival or a strategy session for your next vehicle acquisition, ACE is the single point of contact for everything automotive in your life.
              </p>
            </Card>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-5 rounded-lg text-center">
                <p className="text-3xl font-bold text-black">Sandy Springs</p>
                <p className="text-xs text-gray-500 mt-1">Georgia, USA</p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg text-center">
                <p className="text-3xl font-bold text-black">Metro ATL</p>
                <p className="text-xs text-gray-500 mt-1">Full Coverage Area</p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg text-center">
                <p className="text-3xl font-bold text-red-600">3</p>
                <p className="text-xs text-gray-500 mt-1">Membership Tiers</p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg text-center">
                <p className="text-3xl font-bold text-red-600">24/7</p>
                <p className="text-xs text-gray-500 mt-1">Concierge Access</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Four Pillars */}
      <div className="bg-black text-white py-24" data-testid="about-four-pillars">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-red-500 font-semibold tracking-widest mb-3">THE STANDARD</p>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Four Pillars of <em className="text-red-500 not-italic">Luxury Service</em></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Every ACE interaction is measured against these four pillars. They are non-negotiable, and they define what separates a car service from a concierge service.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {fourPillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <div key={idx} className="text-center group" data-testid={`about-pillar-${idx}`}>
                  <div className="h-16 w-16 mx-auto rounded-full border-2 border-red-600 flex items-center justify-center mb-5 group-hover:bg-red-600 transition-colors duration-300">
                    <Icon className="h-7 w-7 text-red-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{pillar.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Six Pillars of ACE Platform */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <p className="text-sm text-red-600 font-semibold tracking-widest mb-3">THE PLATFORM</p>
          <h2 className="text-4xl font-bold text-black mb-4" data-testid="six-pillars-heading">Six Pillars of the <em className="text-red-600 not-italic">ACE Ecosystem</em></h2>
          <p className="text-gray-600 max-w-3xl mx-auto">ACE is evolving from a luxury car service into a full automotive lifestyle platform. These six verticals define the strategic direction.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sixPillars.map((pillar, idx) => {
            const Icon = pillar.icon;
            return (
              <Card key={idx} className="p-6 border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1" data-testid={`platform-pillar-${idx}`}>
                <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                  <Icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{pillar.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{pillar.description}</p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Strategic Direction */}
      <div className="bg-gray-50 py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-sm text-red-600 font-semibold tracking-widest mb-3">STRATEGIC DIRECTION</p>
            <h2 className="text-4xl font-bold text-black mb-4">Where ACE Is <em className="text-red-600 not-italic">Going</em></h2>
          </div>
          <div className="space-y-6 text-gray-600 leading-relaxed">
            <p>
              ACE is scaling deliberately — not rapidly. The priority is maintaining the concierge standard while expanding capabilities that clients actually need. Every new initiative answers the same question: <strong className="text-black">does this make the client's auto lifestyle simpler, smarter, or more enjoyable?</strong>
            </p>
            <p>
              Current focus areas include expanding the exotic vehicle collection, deepening the AuctionWell intelligence platform for members, and building corporate partnership programs that extend ACE-level service to employee benefit packages and property management clients across Metro Atlanta.
            </p>
            <p>
              The long-term vision is a single point of contact for every automotive decision in a client's life — from the car they buy, to the way it's maintained, to the person who drives them when they need to be driven.
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Start the conversation.</h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8">
            Every ACE relationship begins with a free Discovery Call. No pitch. No pressure. Just clarity on whether ACE is the right fit.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={() => navigate('/contact')}
              size="lg"
              className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg"
              data-testid="about-cta-btn"
            >
              Book a Discovery Call
            </Button>
            <a href="tel:6788463009">
              <Button 
                size="lg"
                variant="outline"
                className="h-14 px-8 border-white text-white hover:bg-white hover:text-black text-lg"
              >
                <Phone className="mr-2 h-5 w-5" /> (678) 846-3009
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
